import React, {useEffect, useRef, useState} from "react";
import {EventResult} from "event-series-shared/src/types/EventResult";
import {getEventSeries} from "../Api";
import DetailsModal from "./DetailsModal";
import EditEventModal from "./EditEventModal";

const HamburgerIcon = () => (
    <svg width="14" height="11" viewBox="0 0 14 11" fill="currentColor">
        <rect x="0" y="0"  width="14" height="2" rx="1"/>
        <rect x="0" y="4.5" width="14" height="2" rx="1"/>
        <rect x="0" y="9"  width="14" height="2" rx="1"/>
    </svg>
);

const PencilIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 1.5L10.5 3.5L3.5 10.5H1.5V8.5L8.5 1.5Z"/>
        <line x1="7" y1="3" x2="9" y2="5"/>
    </svg>
);

const MAX_EVENT_COLOR = 8;
const getEventColor = (count: number): string => {
    if (count === 0) return '';
    const t = Math.min((count - 1) / (MAX_EVENT_COLOR - 1), 1);
    const hue = Math.round(200 * (1 - t));
    const lightness = Math.round(60 - t * 15);
    return `hsl(${hue}, 90%, ${lightness}%)`;
};

function EventSeries() {

    const [eventResults, setEventResults] = useState<EventResult[]>([]);
    const [currentEvents, setCurrentEvents] = useState<EventResult[]>([]);
    const [modalY, setModalY] = useState<number>(0);
    const [modalX, setModalX] = useState<number>(0);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [mode, setMode] = useState<'12' | '24'>('12');
    const [eventToEdit, setEventToEdit] = useState<EventResult | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scheduleHide = () => {
        hideTimeoutRef.current = setTimeout(() => setCurrentEvents([]), 150);
    };

    const cancelHide = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    const loadEvents = () => {
        getEventSeries(1).then(result => setEventResults(result.data));
    };

    useEffect(() => {
        loadEvents();
    }, []);

    const hours: number[] = [];
    if (mode === '12') {
        for (let hour = 1; hour <= 12; hour++) hours.push(hour);
    } else {
        for (let hour = 0; hour <= 23; hour++) hours.push(hour);
    }

    const minutes: number[] = [];
    for (let minute = 0; minute < 60; minute++) {
        minutes.push(minute);
    }

    const minuteLabels: string[] = [];
    for (let minuteLabel = 0; minuteLabel < 60; minuteLabel += 5) {
        const label: string = minuteLabel < 10 ? "0" + minuteLabel : "" + minuteLabel
        minuteLabels.push(label);
    }

    const renderHour = (hour: number) => {
        return (
            <div className={"hour"}>
                 <div className={getHourTitleClass(hour)}>{hour}</div>
                 <div className={"hour-bar"}>
                     {renderMinuteLabel()}
                     <div className={"squares"} onMouseOut={scheduleHide}>
                        {minutes.map(minute => renderMinute(hour, minute))}
                     </div>
                </div>
            </div>
        )
    }

    const getHourTitleClass = (hour: number): string => {
        for (let minute = 0; minute < 60; minute++) {
            if (findEventsDuringMinute(hour, minute).length === 0) return 'hour-title'
        }
        return 'hour-title completed'
    }

    const makeLabel = (hour: number, minute: number) => {
        return hour + ":" + (minute < 10 ? "0" + minute : "" + minute);
    }

    const renderMinute = (hour: number, minute: number) => {
        const eventsDuringMinute: EventResult[] = findEventsDuringMinute(hour, minute);
        const count = eventsDuringMinute.length;
        const startClass = isStart(hour, minute, eventsDuringMinute) ? "event-start" : "";
        const endClass = isEnd(hour, minute, eventsDuringMinute) ? "event-end" : "";
        const hasEventClass = count > 0 ? "has-event" : "";
        const color = getEventColor(count);
        return (
            <span className={"minute " + (count === 0 ? "events-0 " : "") + startClass + " " + endClass + " " + hasEventClass + " " + getBorderClass(eventsDuringMinute)}
                  style={count > 0 ? {backgroundColor: color, border: `1px solid ${color}`} : {}}
                  onMouseOver={(mouseEvent) => setEventModal(mouseEvent, eventsDuringMinute)}
            />
        )
    }

    const NUM_BORDER_CLASSES = 5;
    const getBorderClass = (events: EventResult[]): string => {
        if (events.length === 0) return "";

        const classIndex = getIndexOfEvent(events[0]) % NUM_BORDER_CLASSES;
        return "border-" + classIndex;
    }

    const getIndexOfEvent = (currentEvent: EventResult): number => {
        for (let i = 0; i < eventResults.length; i++) {
            if (currentEvent.eventId === eventResults[i].eventId) return i;
        }
        return -1;
    }

    const setEventModal = (mouseEvent: React.MouseEvent<HTMLSpanElement, MouseEvent>, events: EventResult[]) => {
        const rect = mouseEvent.currentTarget.getBoundingClientRect();
        let newModalY = rect.top + window.scrollY - 50;
        if (newModalY <= 0) newModalY = rect.top + window.scrollY + 50;
        setModalY(newModalY);

        let newModalX = rect.right + window.scrollX - 50;
        if (newModalX <= 0) newModalX = rect.right + window.scrollX + 50;
        setModalX(newModalX);

        setCurrentEvents(events);
    }

    const isStart = (hour: number, minute: number, eventsDuringMinute: EventResult[]): boolean => {
        for (let i = 0; i < eventsDuringMinute.length; i++) {
            const evHour = mode === '24' ? eventsDuringMinute[i].startHour24 : eventsDuringMinute[i].startHour;
            if (evHour === hour && eventsDuringMinute[i].startMinute === minute) return true;
        }
        return false;
    }

    const isEnd = (hour: number, minute: number, eventsDuringMinute: EventResult[]): boolean => {
        for (let i = 0; i < eventsDuringMinute.length; i++) {
            const evHour = mode === '24' ? eventsDuringMinute[i].endHour24 : eventsDuringMinute[i].endHour;
            if (evHour === hour && eventsDuringMinute[i].endMinute === minute) return true;
        }
        return false;
    }

    const findEventsDuringMinute = (hour: number, minute: number): EventResult[] => {
        const eventsDuringMinute: EventResult[] = [];
        for (let i = 0; i < eventResults.length; i++) {
            if (eventIsDuringMinute(hour, minute, eventResults[i])) eventsDuringMinute.push(eventResults[i]);
        }
        return eventsDuringMinute;
    }

    const eventIsDuringMinute = (hour: number, minute: number, eventResult: EventResult): boolean => {
        if (mode === '24') {
            return isDuringMinute(hour, minute, eventResult.startHour24, eventResult.startMinute, eventResult.endHour24, eventResult.endMinute);
        }
        return isDuringMinute(hour, minute, eventResult.startHour, eventResult.startMinute, eventResult.endHour, eventResult.endMinute);
    }

    const isDuringMinute = (hour: number, minute: number, startHour: number, startMinute: number, endHour: number, endMinute: number): boolean => {
        const wrapsAround = startHour > endHour ||
            (startHour === endHour && startMinute > endMinute);

        const startBefore = startHour < hour ||
            (startHour === hour && startMinute <= minute);
        const endAfter = endHour > hour ||
            (endHour === hour && endMinute >= minute);

        if (wrapsAround) {
            // example - the event could be 12:58 to 1:04, then 1:02 and 12:59 should return true
            // so we return true if the event is after the start OR before the end
            return startBefore || endAfter;
        } else {
            return startBefore && endAfter;
        }
    }

    const renderProgressBar = () => {
        const totalMinutes = hours.length * minutes.length;
        let coveredMinutes = 0;
        for (const hour of hours) {
            for (const minute of minutes) {
                if (findEventsDuringMinute(hour, minute).length > 0) coveredMinutes++;
            }
        }
        const pct = totalMinutes > 0 ? (coveredMinutes / totalMinutes) * 100 : 0;
        return (
            <div className="progress-bar-container">
                <div className="progress-bar-fill" style={{width: `${pct}%`}} />
                <span className="progress-bar-label">{Math.round(pct)}% ({coveredMinutes} / {totalMinutes} minutes)</span>
            </div>
        );
    }

    const renderMinuteLabel = () => {
        return (
            <div className={"minute-labels"}>
                {minuteLabels.map(ml => <span>{ml}</span>)}
            </div>
        )
    }

    return (
        <div className={"event-series"}>
            <div className={"bottom-bar"}>
                <button className="details-btn" onClick={() => setShowDetailsModal(true)}><HamburgerIcon /></button>
                {renderProgressBar()}
                <div className={"legend"}>
                    {[1,2,3,4,5,6,7,8].map(n => {
                        const c = getEventColor(n);
                        return (
                            <React.Fragment key={n}>
                                <span className={"legend-num"}>{n === 8 ? '8+' : n}</span>
                                <span className={"legend-square"} style={{backgroundColor: c, border: `1px solid ${c}`}}></span>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            <div className="hours-scroll">
                {hours.map(renderHour)}
            </div>

            <div style={{left: modalX, top: modalY}}
                 className={currentEvents.length ? "events-modal" : "hidden"}
                 onMouseEnter={cancelHide}
                 onMouseLeave={() => setCurrentEvents([])}>
                {currentEvents.map(ev => (
                    <div key={ev.eventId} className="event-row">
                        <span>{String(ev.start).slice(0, String(ev.start).indexOf('T'))} | {makeLabel(mode === '24' ? ev.startHour24 : ev.startHour, ev.startMinute)} – {makeLabel(mode === '24' ? ev.endHour24 : ev.endHour, ev.endMinute)}</span>
                        <button className="delete-btn" onClick={() => { setCurrentEvents([]); setEventToEdit(ev); }}><PencilIcon /></button>
                    </div>
                ))}
            </div>

            {showDetailsModal && <DetailsModal events={eventResults} seriesId={1} mode={mode} onModeChange={setMode} onEventAdded={loadEvents} onEditRequest={ev => { setShowDetailsModal(false); setEventToEdit(ev); }} onClose={() => setShowDetailsModal(false)} />}
            {eventToEdit && <EditEventModal event={eventToEdit} onSaved={() => { setEventToEdit(null); setCurrentEvents([]); loadEvents(); }} onClose={() => setEventToEdit(null)} />}
        </div>
    )
}

export default EventSeries;
